import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  Fade,
  Zoom,
  Slide,
} from '@mui/material';
import {
  Star as StarIcon,
  EmojiEvents as TrophyIcon,
  MilitaryTech as MedalIcon,
  Close as CloseIcon,
  Share as ShareIcon,
  Celebration as CelebrationIcon,
} from '@mui/icons-material';
import { styled, alpha, useTheme, keyframes } from '@mui/material/styles';

// Keyframe animations
const sparkle = keyframes`
  0% { transform: scale(0) rotate(0deg); opacity: 0; }
  50% { transform: scale(1) rotate(180deg); opacity: 1; }
  100% { transform: scale(0) rotate(360deg); opacity: 0; }
`;

const bounce = keyframes`
  0%, 20%, 53%, 80%, 100% { transform: translate3d(0,0,0); }
  40%, 43% { transform: translate3d(0, -20px, 0); }
  70% { transform: translate3d(0, -10px, 0); }
  90% { transform: translate3d(0, -4px, 0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
`;

const confettiDrop = keyframes`
  0% { 
    transform: translateY(-100vh) rotate(0deg); 
    opacity: 1; 
  }
  100% { 
    transform: translateY(100vh) rotate(720deg); 
    opacity: 0; 
  }
`;

// Dynamic glow pulse that adapts to theme
const glowPulse = (iconcolor, theme) => keyframes`
  0%, 100% { 
    box-shadow: 0 0 20px ${alpha(iconcolor, theme.palette.mode === 'dark' ? 0.4 : 0.2)};
  }
  50% { 
    box-shadow: 
      0 0 40px ${alpha(iconcolor, theme.palette.mode === 'dark' ? 0.6 : 0.4)}, 
      0 0 60px ${alpha(iconcolor, theme.palette.mode === 'dark' ? 0.4 : 0.2)};
  }
`;

// Styled components with improved light mode support
const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: theme.spacing(3),
    background: theme.palette.mode === 'dark' 
      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`
      : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
    backdropFilter: 'blur(20px)',
    border: theme.palette.mode === 'dark'
      ? `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
      : `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
    boxShadow: theme.palette.mode === 'dark'
      ? `0 20px 60px ${alpha(theme.palette.common.black, 0.5)}`
      : `0 20px 60px ${alpha(theme.palette.common.black, 0.15)}`,
    overflow: 'visible',
    position: 'relative',
    maxWidth: '500px',
    width: '90vw',
  },
}));

const AchievementContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  textAlign: 'center',
  padding: theme.spacing(4),
  overflow: 'hidden',
  background: theme.palette.mode === 'dark' 
    ? 'transparent'
    : alpha(theme.palette.background.paper, 0.8),
}));

const IconContainer = styled(Box)(({ theme, iconcolor }) => {
  const glowAnimation = glowPulse(iconcolor, theme);
  
  return {
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 120,
    height: 120,
    borderRadius: '50%',
    background: theme.palette.mode === 'dark'
      ? `linear-gradient(135deg, ${alpha(iconcolor, 0.2)}, ${alpha(iconcolor, 0.4)})`
      : `linear-gradient(135deg, ${alpha(iconcolor, 0.1)}, ${alpha(iconcolor, 0.2)})`,
    border: `3px solid ${iconcolor}`,
    animation: `${bounce} 2s infinite, ${glowAnimation} 3s infinite`,
    marginBottom: theme.spacing(3),
    '&::before': {
      content: '""',
      position: 'absolute',
      top: -10,
      left: -10,
      right: -10,
      bottom: -10,
      borderRadius: '50%',
      background: theme.palette.mode === 'dark'
        ? `radial-gradient(circle, ${alpha(iconcolor, 0.1)}, transparent 70%)`
        : `radial-gradient(circle, ${alpha(iconcolor, 0.05)}, transparent 70%)`,
      animation: `${pulse} 2s infinite`,
      zIndex: -1,
    },
  };
});

const SparkleContainer = styled(Box)({
  position: 'absolute',
  width: '100%',
  height: '100%',
  top: 0,
  left: 0,
  pointerEvents: 'none',
  overflow: 'hidden',
});

const Sparkle = styled(Box)(({ delay, color, theme }) => ({
  position: 'absolute',
  width: 8,
  height: 8,
  background: color,
  borderRadius: '50%',
  opacity: theme.palette.mode === 'dark' ? 1 : 0.8,
  boxShadow: theme.palette.mode === 'dark' 
    ? `0 0 6px ${color}`
    : `0 0 4px ${alpha(color, 0.6)}`,
  animation: `${sparkle} 2s infinite`,
  animationDelay: `${delay}s`,
}));

const ConfettiPiece = styled(Box)(({ delay, color, left, theme }) => ({
  position: 'absolute',
  width: 8,
  height: 8,
  background: color,
  left: `${left}%`,
  top: '-10px',
  opacity: theme.palette.mode === 'dark' ? 1 : 0.9,
  boxShadow: theme.palette.mode === 'dark' 
    ? `0 0 4px ${color}`
    : `0 0 3px ${alpha(color, 0.5)}`,
  animation: `${confettiDrop} 3s linear infinite`,
  animationDelay: `${delay}s`,
}));

const PulsingText = styled(Typography)(({ theme }) => ({
  animation: `${pulse} 2s infinite`,
  background: theme.palette.mode === 'dark'
    ? `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
    : `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 800,
  textShadow: theme.palette.mode === 'light' 
    ? `0 1px 3px ${alpha(theme.palette.common.black, 0.1)}`
    : 'none',
}));

// Enhanced icon colors for better light mode visibility
const iconMap = {
  Star: { 
    component: StarIcon, 
    color: '#F59E0B', // Warmer, more visible gold
    lightColor: '#D97706' // Darker gold for light mode
  },
  EmojiEvents: { 
    component: TrophyIcon, 
    color: '#EF4444', // Brighter red
    lightColor: '#DC2626' // Darker red for light mode
  },
  MilitaryTech: { 
    component: MedalIcon, 
    color: '#8B5CF6', // Purple
    lightColor: '#7C3AED' // Darker purple for light mode
  },
};

const AchievementCelebrationDialog = ({ 
  open, 
  onClose, 
  achievement, 
  onShare 
}) => {
  const theme = useTheme();
  const [showConfetti, setShowConfetti] = useState(false);
  const [sparkles, setSparkles] = useState([]);
  const [confetti, setConfetti] = useState([]);

  // Enhanced color palette for light mode
  const getSparkleColors = () => {
    if (theme.palette.mode === 'dark') {
      return ['#FFD700', '#FF6B35', '#8B5CF6', '#4CAF50', '#E91E63'];
    } else {
      return ['#F59E0B', '#EF4444', '#7C3AED', '#059669', '#DB2777'];
    }
  };

  const getConfettiColors = () => {
    if (theme.palette.mode === 'dark') {
      return ['#FFD700', '#FF6B35', '#8B5CF6', '#4CAF50', '#E91E63', '#00BCD4'];
    } else {
      return ['#F59E0B', '#EF4444', '#7C3AED', '#059669', '#DB2777', '#0891B2'];
    }
  };

  // Generate sparkles and confetti on mount
  useEffect(() => {
    if (open) {
      const sparkleColors = getSparkleColors();
      const confettiColors = getConfettiColors();

      // Generate sparkles
      const newSparkles = Array.from({ length: 12 }, (_, i) => ({
        id: i,
        delay: Math.random() * 2,
        top: Math.random() * 100,
        left: Math.random() * 100,
        color: sparkleColors[i % sparkleColors.length],
      }));
      setSparkles(newSparkles);

      // Generate confetti
      const newConfetti = Array.from({ length: 20 }, (_, i) => ({
        id: i,
        delay: Math.random() * 3,
        left: Math.random() * 100,
        color: confettiColors[i % confettiColors.length],
      }));
      setConfetti(newConfetti);
      setShowConfetti(true);

      // Auto-hide confetti after animation
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [open, theme.palette.mode]);

  if (!achievement) return null;

  const iconInfo = iconMap[achievement.icon_name] || iconMap.Star;
  const IconComponent = iconInfo.component;
  const iconColor = theme.palette.mode === 'dark' ? iconInfo.color : iconInfo.lightColor;

  const handleShare = () => {
    if (onShare) {
      onShare(achievement);
    } else {
      // Default share behavior
      const text = `I just earned the "${achievement.title}" achievement worth ${achievement.points} points!`;
      if (navigator.share) {
        navigator.share({
          title: 'Achievement Unlocked!',
          text: text,
        });
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(text);
      }
    }
  };

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      TransitionComponent={Zoom}
      TransitionProps={{
        timeout: {
          enter: 500,
          exit: 300,
        },
      }}
    >
      <DialogContent sx={{ p: 0, position: 'relative' }}>
        {/* Close button with improved light mode styling */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 10,
            background: theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.paper, 0.8)
              : alpha(theme.palette.background.paper, 0.9),
            backdropFilter: 'blur(10px)',
            border: theme.palette.mode === 'light' 
              ? `1px solid ${alpha(theme.palette.divider, 0.2)}`
              : 'none',
            '&:hover': {
              background: theme.palette.mode === 'dark'
                ? alpha(theme.palette.background.paper, 0.9)
                : alpha(theme.palette.background.paper, 1),
              transform: 'scale(1.05)',
            },
            transition: 'all 0.2s ease',
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Sparkles with theme-aware colors */}
        <SparkleContainer>
          {sparkles.map((sparkle) => (
            <Sparkle
              key={sparkle.id}
              delay={sparkle.delay}
              color={sparkle.color}
              theme={theme}
              sx={{
                top: `${sparkle.top}%`,
                left: `${sparkle.left}%`,
              }}
            />
          ))}
        </SparkleContainer>

        {/* Confetti with theme-aware colors */}
        {showConfetti && (
          <SparkleContainer>
            {confetti.map((piece) => (
              <ConfettiPiece
                key={piece.id}
                delay={piece.delay}
                color={piece.color}
                left={piece.left}
                theme={theme}
              />
            ))}
          </SparkleContainer>
        )}

        <AchievementContainer>
          {/* Achievement Unlocked Header with better light mode contrast */}
          <Slide direction="down" in={open} timeout={800}>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  color: theme.palette.mode === 'dark' 
                    ? theme.palette.primary.main
                    : theme.palette.primary.dark,
                  fontWeight: 700,
                  mb: 2,
                  textShadow: theme.palette.mode === 'light' 
                    ? `0 1px 2px ${alpha(theme.palette.common.black, 0.1)}`
                    : 'none',
                  letterSpacing: '0.5px',
                }}
              >
                🎉 ACHIEVEMENT UNLOCKED! 🎉
              </Typography>
            </Box>
          </Slide>

          {/* Icon with theme-aware colors */}
          <Fade in={open} timeout={1000}>
            <IconContainer iconcolor={iconColor} theme={theme}>
              <IconComponent 
                sx={{ 
                  fontSize: 60, 
                  color: iconColor,
                  filter: theme.palette.mode === 'dark'
                    ? 'drop-shadow(0 0 10px rgba(255,255,255,0.5))'
                    : 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                }} 
              />
            </IconContainer>
          </Fade>

          {/* Achievement Title with improved readability */}
          <Slide direction="up" in={open} timeout={1200}>
            <Box>
              <PulsingText variant="h4" sx={{ mb: 2 }}>
                {achievement.title}
              </PulsingText>
            </Box>
          </Slide>

          {/* Description with better contrast */}
          <Fade in={open} timeout={1400}>
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                maxWidth: '80%',
                mx: 'auto',
                lineHeight: 1.6,
                color: theme.palette.mode === 'dark' 
                  ? theme.palette.text.secondary
                  : theme.palette.text.primary,
                fontWeight: theme.palette.mode === 'light' ? 500 : 400,
              }}
            >
              {achievement.description}
            </Typography>
          </Fade>

          {/* Points Badge with enhanced styling */}
          <Zoom in={open} timeout={1600}>
            <Box sx={{ mb: 3 }}>
              <Chip
                icon={<CelebrationIcon />}
                label={`${achievement.points} Points Earned!`}
                color="primary"
                variant="filled"
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  py: 3,
                  px: 2,
                  height: 'auto',
                  background: theme.palette.mode === 'dark'
                    ? `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                    : `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                  boxShadow: theme.palette.mode === 'dark'
                    ? `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                    : `0 4px 20px ${alpha(theme.palette.primary.main, 0.25)}`,
                  animation: `${pulse} 2s infinite`,
                  '& .MuiChip-icon': {
                    fontSize: '1.2rem',
                    color: 'white',
                  },
                  '& .MuiChip-label': {
                    color: 'white',
                  },
                }}
              />
            </Box>
          </Zoom>

          {/* Action Buttons with improved light mode styling */}
          <Slide direction="up" in={open} timeout={1800}>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="outlined"
                startIcon={<ShareIcon />}
                onClick={handleShare}
                sx={{
                  borderRadius: 25,
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 600,
                  borderWidth: 2,
                  borderColor: theme.palette.mode === 'dark' 
                    ? theme.palette.primary.main
                    : theme.palette.primary.dark,
                  color: theme.palette.mode === 'dark' 
                    ? theme.palette.primary.main
                    : theme.palette.primary.dark,
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: theme.palette.mode === 'dark' 
                      ? theme.palette.primary.light
                      : theme.palette.primary.main,
                    background: theme.palette.mode === 'dark'
                      ? alpha(theme.palette.primary.main, 0.1)
                      : alpha(theme.palette.primary.main, 0.05),
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Share Achievement
              </Button>
              <Button
                variant="contained"
                onClick={onClose}
                sx={{
                  borderRadius: 25,
                  px: 4,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 700,
                  background: theme.palette.mode === 'dark'
                    ? `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                    : `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                  boxShadow: theme.palette.mode === 'dark'
                    ? `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                    : `0 6px 20px ${alpha(theme.palette.primary.main, 0.25)}`,
                  '&:hover': {
                    background: theme.palette.mode === 'dark'
                      ? `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`
                      : `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    transform: 'translateY(-2px)',
                    boxShadow: theme.palette.mode === 'dark'
                      ? `0 8px 25px ${alpha(theme.palette.primary.main, 0.4)}`
                      : `0 8px 25px ${alpha(theme.palette.primary.main, 0.3)}`,
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                Awesome!
              </Button>
            </Box>
          </Slide>

          {/* Achievement Date with improved visibility */}
          {achievement.achieved_at && (
            <Fade in={open} timeout={2000}>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  mt: 3,
                  color: theme.palette.mode === 'dark'
                    ? theme.palette.text.secondary
                    : theme.palette.text.secondary,
                  fontWeight: theme.palette.mode === 'light' ? 500 : 400,
                  opacity: theme.palette.mode === 'dark' ? 0.7 : 0.8,
                }}
              >
                Earned on {new Date(achievement.achieved_at).toLocaleDateString()}
              </Typography>
            </Fade>
          )}
        </AchievementContainer>
      </DialogContent>
    </StyledDialog>
  );
};

export default AchievementCelebrationDialog;